import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, MapPin, Layers, Clock } from 'lucide-react';
import { Reveal } from './Reveal';
import { PROJECTS_DATA } from '../data/mock';

/**
 * ProjectsShowcase — replaces the older auto-rotating slider.
 *
 * Layout (desktop)
 *   ┌──────────────────────────────────────┬──────────────────┐
 *   │  FEATURED PROJECT (8 cols)           │  THUMB STACK     │
 *   │  Crisp full image, no heavy gradient │  (4 cols)        │
 *   │  Slim glass card pinned bottom-LEFT  │  Vertical list   │
 *   │  with title · location · product     │  of 4 small      │
 *   │  · "View case study" CTA             │  project cards   │
 *   └──────────────────────────────────────┴──────────────────┘
 *
 * Mobile: featured stacks above the thumb list. No auto-rotation, no
 * progress dots, no prev/next arrows — clicking a thumb swaps the
 * featured.
 */
export default function ProjectsShowcase({ projects }) {
  const items = (projects && projects.length ? projects : PROJECTS_DATA).slice(0, 5);
  const [active, setActive] = useState(0);
  const featured = items[active] || items[0];
  const thumbs = items.filter((_, i) => i !== active);

  return (
    <section
      data-testid="projects-showcase"
      className="relative bg-[#E7E9E4] text-[#1A1A1A] overflow-hidden border-y border-[#1A1A1A]/10"
    >
      {/* Soft sage atmosphere — same blooms used elsewhere on the page */}
      <div className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full bg-[#6A8F3E]/10 blur-[160px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#314F2B]/8 blur-[140px] pointer-events-none" />

      {/* Header */}
      <div className="relative px-6 md:px-12 pt-20 md:pt-28 pb-8 md:pb-10">
        <div
          className="mx-auto flex items-end justify-between gap-6 flex-wrap"
          style={{ maxWidth: '1700px' }}
        >
          <Reveal>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#314F2B] font-bold mb-4">
                Selected projects
              </div>
              <h2 className="font-serif-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[#1A1A1A]">
                Latest{' '}
                <span className="italic font-light text-[#6A8F3E]">works.</span>
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/projects"
              data-cursor="hover"
              data-cursor-label="View"
              data-testid="projects-showcase-all-link"
              className="text-[11px] tracking-[0.3em] uppercase text-[#314F2B] hover:text-[#6A8F3E] link-underline font-semibold inline-flex items-center gap-2"
            >
              View all projects
              <ArrowUpRight size={14} strokeWidth={2.2} />
            </Link>
          </Reveal>
        </div>
      </div>

      {/* Main grid: featured (8) + thumb stack (4) */}
      <div className="relative px-6 md:px-12 pb-20 md:pb-28">
        <div
          className="mx-auto grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-7"
          style={{ maxWidth: '1700px' }}
        >
          {/* Featured tile */}
          <div className="lg:col-span-8">
            <FeaturedTile project={featured} />
          </div>

          {/* Thumb stack */}
          <div className="lg:col-span-4 flex flex-col gap-5 md:gap-6">
            {thumbs.map((p) => {
              const realIdx = items.findIndex((x) => x.slug === p.slug);
              return (
                <ThumbTile
                  key={p.slug}
                  project={p}
                  onClick={() => setActive(realIdx)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ Featured tile ============================ */

function FeaturedTile({ project }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={project.slug}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
        className="relative aspect-[16/11] md:aspect-[16/10] rounded-2xl overflow-hidden bg-[#1A1A1A] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.45)]"
        data-testid="projects-showcase-featured"
      >
        <img
          src={project.after}
          alt={project.title}
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center 50%' }}
        />
        {/* Minimal corner-only darkening so the photo stays crisp */}
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at bottom left, rgba(22,42,30,0.72) 0%, rgba(22,42,30,0.45) 25%, rgba(22,42,30,0) 55%)',
          }}
        />

        {/* Tiny status pill top-right */}
        <div className="absolute top-5 right-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#162A1E]/55 backdrop-blur-md border border-[#E7E9E4]/15">
          <span className="w-1.5 h-1.5 rounded-full bg-[#6A8F3E]" />
          <span className="text-[9px] tracking-[0.32em] uppercase text-[#E7E9E4] font-semibold">
            Install completed
          </span>
        </div>

        {/* Glass info card pinned bottom-LEFT (does not cover full width) */}
        <Link
          to={`/projects/${project.slug}`}
          data-cursor="hover"
          data-cursor-label="View"
          data-testid={`projects-showcase-cta-${project.slug}`}
          className="absolute left-4 right-4 bottom-4 md:left-6 md:bottom-6 md:right-auto md:max-w-[440px] group block"
        >
          <div
            className="rounded-xl p-5 md:p-6 transition-transform group-hover:-translate-y-0.5"
            style={{
              backdropFilter: 'blur(22px) saturate(140%)',
              WebkitBackdropFilter: 'blur(22px) saturate(140%)',
              background:
                'linear-gradient(135deg, rgba(231,233,228,0.10), rgba(22,42,30,0.74))',
              border: '1px solid rgba(231,233,228,0.22)',
              boxShadow:
                '0 30px 60px -25px rgba(22,42,30,0.65), inset 0 1px 0 rgba(231,233,228,0.20)',
            }}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-7 h-px bg-[#A3C66F]" />
              <span className="text-[9.5px] tracking-[0.32em] uppercase text-[#A3C66F] font-bold">
                {project.product}
              </span>
            </div>
            <h3
              className="font-serif-editorial text-2xl md:text-3xl leading-[1.05] tracking-tight text-[#E7E9E4]"
              style={{ textShadow: '0 4px 24px rgba(0,0,0,0.7)' }}
            >
              {project.title.replace('—', '·')}
            </h3>
            <div className="mt-4 flex items-center gap-4 text-[11px] text-[#E7E9E4]/85 font-medium">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={11} className="text-[#A3C66F]" strokeWidth={2} />
                {project.location}
              </span>
              {project.duration && (
                <>
                  <span className="text-[#E7E9E4]/30">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={11} className="text-[#A3C66F]" strokeWidth={2} />
                    {project.duration}
                  </span>
                </>
              )}
            </div>

            <div className="mt-5 inline-flex items-center gap-2.5">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#E7E9E4] group-hover:text-[#A3C66F] font-bold transition-colors">
                View case study
              </span>
              <span className="w-8 h-8 rounded-full border border-[#A3C66F]/50 group-hover:bg-[#A3C66F] group-hover:border-[#A3C66F] flex items-center justify-center transition-all duration-500">
                <ArrowUpRight
                  size={13}
                  className="text-[#A3C66F] group-hover:text-[#162A1E] group-hover:rotate-45 transition-all"
                  strokeWidth={2.4}
                />
              </span>
            </div>
          </div>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
}

/* ============================ Thumb tile ============================ */

function ThumbTile({ project, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-cursor="hover"
      data-cursor-label="Feature"
      data-testid={`projects-showcase-thumb-${project.slug}`}
      className="group relative h-[160px] md:h-[180px] lg:h-[31%] lg:min-h-[140px] w-full rounded-xl overflow-hidden bg-[#1A1A1A] shadow-[0_18px_40px_-20px_rgba(0,0,0,0.4)] hover:-translate-y-0.5 transition-transform duration-500 text-left"
    >
      <img
        src={project.after}
        alt={project.title}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms] ease-out"
        style={{ objectPosition: 'center 55%' }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to top, rgba(22,42,30,0.78) 0%, rgba(22,42,30,0.30) 45%, rgba(22,42,30,0.0) 70%)',
        }}
      />

      <div className="absolute left-4 right-4 bottom-3.5 md:bottom-4">
        <div className="text-[8.5px] tracking-[0.32em] uppercase text-[#A3C66F] font-bold mb-1">
          {project.product}
        </div>
        <div
          className="font-serif-editorial text-base md:text-lg leading-tight text-[#E7E9E4] line-clamp-2"
          style={{ textShadow: '0 2px 10px rgba(0,0,0,0.65)' }}
        >
          {project.title.replace('—', '·')}
        </div>
      </div>

      <span className="absolute top-3.5 right-3.5 w-9 h-9 rounded-full bg-[#162A1E]/55 backdrop-blur-md border border-[#E7E9E4]/15 group-hover:bg-[#A3C66F] group-hover:border-[#A3C66F] flex items-center justify-center transition-all">
        <Layers
          size={12}
          className="text-[#E7E9E4] group-hover:text-[#162A1E] transition-colors"
          strokeWidth={2}
        />
      </span>
    </button>
  );
}
